import type { CoverNoteData } from '../types/coverNote';
import type { CommonCoverNoteResponse } from '../types/coverNoteApi';
import { getMockCoverNote } from '../data/coverNoteMocks';
import type { WizardFlowKind } from '../utils/wizardCategoryRoute';
import { usesCommonCoverNote, usesCriminalAppellateCoverNote } from '../utils/wizardCategoryRoute';
import { downloadCommonCoverNotePdf, getCommonCoverNote } from './coverNoteRequests';
import { caseApiPdfRequest, caseApiRequest } from './apiClient';

export type CoverNoteResult = { success: true; data: CoverNoteData } | { success: false; message: string };

function strField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeCoverNote(raw: Record<string, unknown>, caseRequestId?: string): CoverNoteData {
  const docs = raw.documents ?? raw.documents_declared;
  const docList = Array.isArray(docs) ? docs.map((d) => (typeof d === 'string' ? d : String(d))) : [];

  return {
    caseRequestId: caseRequestId || strField(raw, 'caseRequestId', 'case_request_id') || undefined,
    fileReferenceNumber: strField(raw, 'fileReferenceNumber', 'file_reference_number'),
    requestSubmittedAt: strField(raw, 'requestSubmittedAt', 'request_submitted_at'),
    submittedAgency: strField(raw, 'submittedAgency', 'submitted_agency'),
    submittedDivisionName: strField(raw, 'submittedDivisionName', 'submitted_division_name', 'division_name'),
    fileCategory: strField(raw, 'fileCategory', 'file_category'),
    subFileCategory: strField(raw, 'subFileCategory', 'sub_file_category'),
    applicableAreas: strField(raw, 'applicableAreas', 'applicable_areas'),
    description: strField(raw, 'description'),
    urgencyLevel: strField(raw, 'urgencyLevel', 'urgency_level'),
    deadline: strField(raw, 'deadline'),
    urgencyJustification: strField(raw, 'urgencyJustification', 'urgency_justification'),
    caseSource: strField(raw, 'caseSource', 'case_source'),
    connectedAgdFileReferenceNumber:
      strField(raw, 'connectedAgdFileReferenceNumber', 'connected_agd_file_reference_number'),
    accusedOrSuspectName: strField(raw, 'accusedOrSuspectName', 'accused_or_suspect_name'),
    documents: docList,
    qr: strField(raw, 'qr'),
  };
}

function normalizeCommonToCoverNoteData(raw: CommonCoverNoteResponse, caseRequestId: string): CoverNoteData {
  return {
    caseRequestId,
    fileReferenceNumber: raw.fileReferenceNumber,
    requestSubmittedAt: raw.requestSubmittedAt,
    submittedAgency: raw.submittedAgency,
    submittedDivisionName: raw.submittedDivisionName,
    fileCategory: raw.fileCategory,
    subFileCategory: raw.subFileCategory,
    applicableAreas: (raw.applicableAreas ?? []).join(', '),
    description: raw.description ?? '',
    urgencyLevel: raw.urgencyLevel ?? '',
    deadline: raw.deadline ?? '',
    urgencyJustification: raw.urgencyJustification ?? '',
    caseSource: raw.caseSource ?? '',
    connectedAgdFileReferenceNumber: raw.connectedAgdFileReferenceNumber ?? '',
    accusedOrSuspectName: raw.accusedOrSuspectName ?? '',
    documents: raw.documents ?? [],
    qr: raw.qr,
  };
}

function mockFallback(fileRefFallback: string | undefined, id: string): CoverNoteResult | null {
  if (import.meta.env.VITE_USE_MOCK_API !== 'true') return null;
  const mock = fileRefFallback ? getMockCoverNote(fileRefFallback, id) : null;
  if (mock) return { success: true, data: mock };
  return null;
}

/** CMS-CRC-4 — criminal cover note (same path as extranet). */
export async function getCoverNote(caseRequestId: string, fileRefFallback?: string): Promise<CoverNoteResult> {
  return getCoverNoteByFlow(caseRequestId, 'criminal', fileRefFallback);
}

export async function getCoverNoteByFlow(
  caseRequestId: string,
  flow: WizardFlowKind,
  fileRefFallback?: string,
): Promise<CoverNoteResult> {
  const id = caseRequestId.trim();
  const ref = fileRefFallback?.trim() ?? '';

  if (!id && ref) {
    const mockOnly = mockFallback(ref, '');
    if (mockOnly) return mockOnly;
    return { success: false, message: 'Cover note not found for this file reference.' };
  }

  if (!id) return { success: false, message: 'Case request id is missing.' };

  if (usesCommonCoverNote(flow) || usesCriminalAppellateCoverNote(flow)) {
    const res = await getCommonCoverNote(id);
    if (!res.success) {
      const fallback = mockFallback(ref, id);
      if (fallback) return fallback;
      return { success: false, message: res.message ?? 'Cover note could not be loaded.' };
    }
    return { success: true, data: normalizeCommonToCoverNoteData(res.data, id) };
  }

  const res = await caseApiRequest<unknown>(`/cover-note/${encodeURIComponent(id)}`);
  if (!res.ok) {
    const fallback = mockFallback(ref, id);
    if (fallback) return fallback;
    return { success: false, message: res.message };
  }
  const body = res.data;
  const payload =
    body && typeof body === 'object'
      ? (body as Record<string, unknown>)
      : null;
  if (!payload) {
    return { success: false, message: 'Invalid cover note response.' };
  }

  return { success: true, data: normalizeCoverNote(payload, id) };
}

/** CMS-CRC-5 — criminal cover note PDF (same path as extranet). */
export async function downloadCoverNotePdf(
  caseRequestId: string,
): Promise<{ success: true; data: Blob } | { success: false; message: string }> {
  return downloadCoverNotePdfByFlow(caseRequestId, 'criminal');
}

export async function downloadCoverNotePdfByFlow(
  caseRequestId: string,
  flow: WizardFlowKind,
): Promise<{ success: true; data: Blob } | { success: false; message: string }> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is missing.' };

  if (usesCommonCoverNote(flow) || usesCriminalAppellateCoverNote(flow)) {
    const res = await downloadCommonCoverNotePdf(id);
    if (!res.success) {
      return { success: false, message: res.message || 'Cover note PDF could not be loaded.' };
    }
    return { success: true, data: res.data };
  }

  const res = await caseApiPdfRequest(`/cover-note/pdf/${encodeURIComponent(id)}`);
  if (!res.ok) {
    return { success: false, message: res.message || 'Cover note PDF could not be loaded.' };
  }
  return { success: true, data: res.data };
}
